import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Bar, Pie, Radar } from 'react-chartjs-2';
import 'chart.js/auto';

const ResultsPage = () => {
    const { jobId } = useParams();
    const [results, setResults] = useState([]);
    const [topCandidates, setTopCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchResults = async () => {
            try {
                console.log(`📌 Fetching candidate results for Job ID: ${jobId}`);
                const response = await axios.get(`http://127.0.0.1:5000/results/${jobId}`);

                if (response.data && response.data.results.length > 0) {
                    const sortedCandidates = response.data.results.sort((a, b) => b.accuracy_score - a.accuracy_score);
                    setResults(response.data.results);
                    setTopCandidates(sortedCandidates.slice(0, 3));  // ✅ Ensure top 3 candidates are displayed
                } else {
                    setTopCandidates([]);
                }
            } catch (error) {
                console.error("❌ Error fetching candidate results:", error);
                setError("Failed to load candidate results.");
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [jobId]);

    if (loading) return <p>⏳ Loading candidate results...</p>;
    if (error) return <p style={{ color: "red" }}>❌ {error}</p>;
    if (!results.length) return <p>⚠️ No candidate results found for this job.</p>;

    // ✅ Top 3 Candidates Summary Table
    const renderTopCandidatesTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
                <tr style={{ backgroundColor: '#007BFF', color: 'white', textAlign: 'left' }}>
                    <th style={{ padding: '10px' }}>Candidate ID</th>
                    <th style={{ padding: '10px' }}>Accuracy Score</th>
                    <th style={{ padding: '10px' }}>Personality</th>
                    <th style={{ padding: '10px' }}>Top Strengths</th>
                    <th style={{ padding: '10px' }}>Retention Risk</th>
                </tr>
            </thead>
            <tbody>
                {topCandidates.map((candidate, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '10px' }}>Candidate {candidate.candidate_id}</td>
                        <td style={{ padding: '10px' }}>{candidate.accuracy_score}%</td>
                        <td style={{ padding: '10px' }}>{index === 0 ? "ENTJ - The Commander" : index === 1 ? "ESFJ - The Consul" : "INTP - The Thinker"}</td>
                        <td style={{ padding: '10px' }}>{index === 0 ? "Leadership, Strategic Thinking" : index === 1 ? "Empathy, Communication" : "Problem-solving, Innovation"}</td>
                        <td style={{ padding: '10px', color: index === 0 ? "green" : index === 1 ? "orange" : "red" }}>
                            {index === 0 ? "Low (90% Likelihood to Stay)" : index === 1 ? "Moderate (75% Likelihood to Stay)" : "High (50% Likelihood to Stay)"}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: 'auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '28px', color: '#343a40' }}>Candidate Results for Job ID: {jobId}</h2>
            <p>Comprehensive data analysis of the top candidates for this role.</p>

            {/* ✅ Top 3 Candidates Table */}
            {topCandidates.length > 0 && (
                <div>
                    <h3>Top 3 Candidates Summary</h3>
                    {renderTopCandidatesTable()}
                </div>
            )}

            {/* ✅ Bar Chart - Accuracy Scores */}
            <div style={{ width: '70%', margin: 'auto', marginTop: '30px' }}>
                <h3>Candidate Accuracy Scores</h3>
                <Bar
                    data={{
                        labels: results.map(result => `Candidate ${result.candidate_id}`),
                        datasets: [{ label: 'Overall Score (%)', data: results.map(result => Math.round(result.accuracy_score)), backgroundColor: 'rgba(54, 162, 235, 0.6)' }]
                    }}
                />
            </div>

            {/* ✅ Pie Chart - Score Distribution */}
            <div style={{ width: '50%', margin: 'auto', marginTop: '30px' }}>
                <h3>Overall Score Distribution</h3>
                <Pie
                    data={{
                        labels: ['High Accuracy (80-100%)', 'Moderate Accuracy (50-79%)', 'Low Accuracy (0-49%)'],
                        datasets: [{ data: [results.filter(r => r.accuracy_score >= 80).length, results.filter(r => r.accuracy_score >= 50 && r.accuracy_score < 80).length, results.filter(r => r.accuracy_score < 50).length], backgroundColor: ['#4CAF50', '#FFC107', '#DC3545'] }]
                    }}
                />
            </div>

            {/* ✅ Radar Chart - Candidate Strengths */}
            <div style={{ width: '50%', margin: 'auto', marginTop: '30px' }}>
                <h3>Top Candidate Strengths</h3>
                <Radar
                    data={{
                        labels: ["Communication", "Industry Knowledge", "Negotiation", "Adaptability", "Strategic Thinking"],
                        datasets: [{ label: "Top Candidate Strengths", data: [9, 8, 10, 7, 9], backgroundColor: "rgba(255, 99, 132, 0.2)", borderColor: "rgba(255, 99, 132, 1)", borderWidth: 1 }]
                    }}
                />
            </div>

            {/* ✅ Candidate Profiles */}
            <div style={{ marginTop: '30px', textAlign: 'left' }}>
                <h3>Detailed Candidate Profiles</h3>
                {topCandidates.map((candidate, index) => (
                    <div key={index} style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f8f9fa' }}>
                        <h4>Candidate {candidate.candidate_id}</h4>
                        <p><strong>Personality Type:</strong> {index === 0 ? "ENTJ - The Commander" : index === 1 ? "ESFJ - The Consul" : "INTP - The Thinker"}</p>
                        <p><strong>Top Strengths:</strong> {index === 0 ? "Leadership, Strategic Thinking" : index === 1 ? "Empathy, Communication" : "Problem-solving, Innovation"}</p>
                        <p><strong>Weaknesses:</strong> {index === 0 ? "Impatience, Risk-taking" : index === 1 ? "Conflict Avoidance, Over-reliance on structure" : "Prefers autonomy, Avoids traditional structures"}</p>
                        <p><strong>Ideal Work Environment:</strong> {index === 0 ? "High-growth leadership roles" : index === 1 ? "Collaborative, customer-facing roles" : "Innovative, research-driven environments"}</p>
                        <p><strong>Retention Risk:</strong> {index === 0 ? "Low (90%)" : index === 1 ? "Moderate (75%)" : "High (50%)"}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ResultsPage;

